import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, BookOpen, CornerDownLeft, Loader2 } from "lucide-react";

// Il cerca in alto: una casella sola per pagine, istruzioni del manuale e
// dati. Chi sta imparando l'app scrive «ferie» o «estintori» e vede dove si
// fa, come si fa e cosa c'e' gia' dentro. Si apre dal pulsante o con Ctrl+K.
//
// I risultati arrivano dal server (/cerca), gia' filtrati sul ruolo: qui si
// mostrano e basta. Ogni riga porta a una pagina; le voci del manuale portano
// alla pagina di cui parlano, e se non ne hanno una ai Manuali.
export default function CercaGlobale() {
  const [aperto, setAperto] = useState(false);
  const [q, setQ] = useState("");
  const [esito, setEsito] = useState(null);
  const [carico, setCarico] = useState(false);
  const campo = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const tasto = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAperto(true);
      }
    };
    window.addEventListener("keydown", tasto);
    return () => window.removeEventListener("keydown", tasto);
  }, []);

  useEffect(() => {
    if (!aperto) return undefined;
    const testo = q.trim();
    if (testo.length < 2) { setEsito(null); return undefined; }
    let vivo = true;
    const t = setTimeout(async () => {
      setCarico(true);
      try {
        const { data } = await api.get("/cerca", { params: { q: testo } });
        if (vivo) setEsito(data);
      } catch {
        if (vivo) setEsito(null);
      } finally {
        if (vivo) setCarico(false);
      }
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [q, aperto]);

  useEffect(() => {
    if (aperto) setTimeout(() => campo.current?.focus(), 50);
    else { setQ(""); setEsito(null); }
  }, [aperto]);

  const vai = (percorso) => {
    setAperto(false);
    nav(percorso || "/manuali");
  };

  // Tutte le righe in fila, per Invio sulla prima.
  const righe = esito ? [
    ...esito.guida.map((g) => ({ percorso: g.percorso })),
    ...esito.dati.flatMap((gr) => gr.righe.map((r) => ({ percorso: r.percorso }))),
  ] : [];
  const niente = esito && righe.length === 0 && !carico;

  return (
    <>
      {/* Sul telefono e' una lente, sul computer una casella: un solo
          pulsante, cambia solo la larghezza. */}
      <button type="button" onClick={() => setAperto(true)} title="Cerca nell'app (Ctrl+K)"
              data-testid="cerca-globale-btn"
              className="inline-flex items-center justify-center lg:justify-start gap-2 h-9 w-9 lg:w-auto lg:min-w-[200px] lg:px-3 rounded-md border border-slate-200 text-sm text-slate-500 hover:bg-slate-50">
        <Search className="w-4 h-4" />
        <span className="hidden lg:inline flex-1 text-left">Cerca...</span>
        <kbd className="hidden lg:inline text-[10px] text-slate-400 border border-slate-200 rounded px-1">Ctrl K</kbd>
      </button>

      <Dialog open={aperto} onOpenChange={setAperto}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden top-[8%] translate-y-0"
                       data-testid="cerca-globale">
          <DialogTitle className="sr-only">Cerca nell&apos;app</DialogTitle>
          {/* A destra resta il posto per la X del dialogo. */}
          <div className="flex items-center gap-2 pl-4 pr-12 h-14 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input ref={campo} value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Cosa cerchi? Una pagina, un dipendente, un mezzo, un controllo..."
                   className="flex-1 h-full outline-none text-base bg-transparent"
                   data-testid="cerca-globale-input"
                   onKeyDown={(e) => {
                     if (e.key === "Enter" && righe[0]) vai(righe[0].percorso);
                   }} />
            {carico && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>

          <div className="max-h-[65vh] overflow-y-auto">
            {!esito && !carico && (
              <div className="px-4 py-6 text-sm text-slate-500 space-y-1">
                <p>Scrivi almeno due lettere. Trova insieme:</p>
                <ul className="list-disc pl-5 text-xs space-y-0.5">
                  <li>le pagine del menu e il pezzo di manuale che le spiega;</li>
                  <li>dipendenti, mezzi, cantieri, articoli, materiali, documenti;</li>
                  <li>controlli, schede e registri dei sistemi di gestione.</li>
                </ul>
                <p className="text-xs pt-1">Invio apre il primo risultato.</p>
              </div>
            )}
            {niente && (
              <div className="px-4 py-6 text-sm text-slate-500">
                Niente con «{esito.q}». Prova con una parola sola, o con un pezzo di nome.
              </div>
            )}

            {esito?.guida?.length > 0 && (
              <div className="py-2">
                <div className="px-4 py-1 text-[10px] uppercase tracking-widest text-slate-400">
                  Pagine e istruzioni
                </div>
                {esito.guida.map((g, i) => (
                  <button key={i} type="button" onClick={() => vai(g.percorso)}
                          className="w-full text-left px-4 py-2 hover:bg-orange-50 flex items-start gap-3"
                          data-testid={`cerca-guida-${i}`}>
                    <BookOpen className={`w-4 h-4 mt-0.5 flex-shrink-0 ${g.genere === "pagina" ? "text-orange-500" : "text-[#0067b2]"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-800">
                        {g.titolo}{g.sotto ? <span className="text-slate-500 font-normal"> › {g.sotto}</span> : null}
                      </span>
                      {g.dove && <span className="block text-[11px] text-slate-400">{g.dove}</span>}
                      {g.estratto && <span className="block text-xs text-slate-600 mt-0.5 line-clamp-2">{g.estratto}</span>}
                    </span>
                    {i === 0 && <CornerDownLeft className="w-3.5 h-3.5 text-slate-300 mt-1 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}

            {esito?.dati?.map((gr) => (
              <div key={gr.nome} className="py-2 border-t border-slate-100">
                <div className="px-4 py-1 text-[10px] uppercase tracking-widest text-slate-400">{gr.nome}</div>
                {gr.righe.map((r, i) => (
                  <button key={i} type="button" onClick={() => vai(r.percorso)}
                          className="w-full text-left px-4 py-1.5 hover:bg-orange-50">
                    <span className="block text-sm text-slate-800">{r.titolo}</span>
                    {r.dettaglio && <span className="block text-xs text-slate-500">{r.dettaglio}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
